// src/utils/sanitizeLabel.js
//
// Repairs mangled Unicode escapes in strings that come from the market
// form-schema data. Emoji written as JS brace-style escapes (e.g. "\u{1F527}")
// are not valid JSON escapes, so MySQL JSON storage drops the backslash and
// labels arrive prefixed with a literal "{u1F527}" (or keep the backslash in
// some rows). Instead of removing the prefix, this converts each mangled
// escape back into the real emoji/character it was meant to be:
//   "{u1F527} Fix / Repair" -> "🔧 Fix / Repair"

// Cache for sanitized strings to avoid repeated processing
const sanitizationCache = new Map();
const CACHE_MAX_SIZE = 1000;

// Matches "{u1F527}" (backslash already lost), "\u{1F527}" (still present),
// and bare "\uXXXX" (JSON escapes are always exactly 4 hex digits, so the
// trailing "(?![0-9A-Fa-f])" keeps something like "\u20A610,000" intact).
const MANGLED_UNICODE_ESCAPE = /\{u([0-9A-Fa-f]{4,6})\}|\\u\{([0-9A-Fa-f]{4,6})\}|\\u([0-9A-Fa-f]{4})(?![0-9A-Fa-f])/g;

// Patterns for cleaning whitespace
const WHITESPACE_PATTERN = /\s+/g;

// Converts a mangled escape match into the real character it represents.
function convertEscape(_match, a, b, c) {
  const hex = a || b || c;
  try {
    return String.fromCodePoint(parseInt(hex, 16));
  } catch (e) {
    return '';
  }
}

/**
 * Extracts emoji from unicode escape sequences if present
 * @param {string} str - The string to process
 * @returns {string} The extracted emoji or empty string
 */
export function extractEmojiFromUnicode(str) {
  if (!str || typeof str !== 'string') return '';

  // Try to extract unicode code point from various formats
  const matches = str.match(/(?:0x|\\u|\{u|U\+)([0-9A-Fa-f]{4,6})/);
  if (matches && matches[1]) {
    try {
      const codePoint = parseInt(matches[1], 16);
      // Only attempt to convert if it's a valid emoji range
      if (codePoint >= 0x1F000 && codePoint <= 0x1FAFF) {
        return String.fromCodePoint(codePoint);
      }
      // Also handle common symbols
      if (codePoint >= 0x2700 && codePoint <= 0x27BF) {
        return String.fromCodePoint(codePoint);
      }
    } catch (e) {
      // Fall through if conversion fails
    }
  }
  return '';
}

/**
 * Cleans a string by converting mangled unicode escapes back into the real
 * characters they represent and normalizing whitespace
 * @param {string} value - The string to sanitize
 * @param {Object} options - Configuration options
 * @param {boolean} options.trimMultiSpace - Whether to trim multiple spaces
 * @param {boolean} options.useCache - Whether to use cache
 * @returns {string} The sanitized string
 */
export function sanitizeString(value, options = {}) {
  const {
    trimMultiSpace = true,
    useCache = true,
  } = options;

  if (typeof value !== 'string') return value;
  if (!value) return value;

  // Check cache if enabled
  if (useCache && sanitizationCache.has(value)) {
    return sanitizationCache.get(value);
  }

  // Convert mangled escapes back to the characters they represent.
  // The emoji is kept inline (e.g. "🔧 Fix / Repair"), never stripped.
  let cleaned = value.replace(MANGLED_UNICODE_ESCAPE, convertEscape);

  // Normalize whitespace
  if (trimMultiSpace) {
    cleaned = cleaned.replace(WHITESPACE_PATTERN, ' ').trim();
  }

  // Cache the result
  if (useCache && sanitizationCache.size < CACHE_MAX_SIZE) {
    sanitizationCache.set(value, cleaned);
  }

  return cleaned;
}

/**
 * Sanitizes a single string, preserving any emoji found in it
 * @param {string} value - The string to process
 * @returns {string} Sanitized string with emoji intact
 */
export function sanitizeWithEmoji(value) {
  return sanitizeString(value);
}

/**
 * Checks if a string contains a unicode escape pattern
 * @param {string} value - The string to check
 * @returns {boolean} True if contains unicode escape
 */
export function hasUnicodeEscape(value) {
  if (typeof value !== 'string') return false;
  MANGLED_UNICODE_ESCAPE.lastIndex = 0;
  return MANGLED_UNICODE_ESCAPE.test(value);
}

/**
 * Recursively sanitizes an object/array schema
 * @param {*} node - The node to sanitize (object, array, or primitive)
 * @param {Object} options - Sanitization options
 * @returns {*} The sanitized node
 */
export function sanitizeSchema(node, options = {}) {
  if (node == null) return node;

  // Handle arrays
  if (Array.isArray(node)) {
    return node.map(item => sanitizeSchema(item, options));
  }

  // Handle objects
  if (node && typeof node === 'object') {
    const out = {};
    for (const [key, val] of Object.entries(node)) {
      out[key] = typeof val === 'string'
        ? sanitizeString(val, options)
        : sanitizeSchema(val, options);
    }
    return out;
  }

  return node;
}

/**
 * Batch sanitize multiple strings at once
 * @param {string[]} strings - Array of strings to sanitize
 * @param {Object} options - Sanitization options
 * @returns {string[]} Array of sanitized strings
 */
export function sanitizeBatch(strings, options = {}) {
  if (!Array.isArray(strings)) return strings;
  return strings.map(str => sanitizeString(str, options));
}

/**
 * Clears the sanitization cache
 * Useful for testing or memory management
 */
export function clearSanitizationCache() {
  sanitizationCache.clear();
}

/**
 * Gets the current cache size
 * @returns {number} Number of cached entries
 */
export function getCacheSize() {
  return sanitizationCache.size;
}

// Default export for backward compatibility
export default sanitizeString;
